import { useEffect, useRef, useState } from 'react';
import { logError } from 'utils/logger';
import { useQuery } from '@apollo/client';
import { useModal } from 'hooks/useModal';
import { format, subDays } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import { ModalsKeys, Nullable } from 'global';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IntegrationSettings } from '@trnsact/trnsact-shared-types';
import { aftermarketPartnerApi } from 'modules/aftermarket/partner/api';
import { Button, Stack } from '@mui/material';
import { PartnerLinksResponse } from 'modules/aftermarket/partner/api/types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { DealerManagementPartnerLink, PartnerLink } from 'modules/aftermarket/partner/types';
import { aftermarketPartnerActions, aftermarketPartnerSelectors } from 'modules/aftermarket/partner/model';
import { DealerManagementTable } from '../../tables';
import { QUERY_INTEGRATION_SETTINGS_ACCOUNT_IS_ADMIN_OF } from '../../../../../../queries/common';

export const DealerManagementCard = () => {
  const history = useHistory();

  const dispatch = useDispatch();

  const account = useSelector((state: any) => state.account);
  const [offset, setOffset] = useState(0);
  const [isLoadingAllPages, setIsLoadingAllPages] = useState(true);
  const [tableResetKey, setTableResetKey] = useState<number>(0);
  const partnerLinks = useSelector(aftermarketPartnerSelectors.partnerLinks);

  const { handleOpen: handleAddDealerDialogOpen } = useModal(ModalsKeys.AftermarketPartnerAddDealerDialog);
  const { handleOpen: openIntegrationSettingsDialog } = useModal(ModalsKeys.IntegrationSettingsDialog);

  const isRefetchingAfterAdd = useRef(false);

  const compareByAccountName = (a: PartnerLink, b: PartnerLink) =>
    a?.accountName?.toLowerCase()?.localeCompare(b?.accountName?.toLowerCase());

  const fetchLazyMode = (data: any) => {
    if (isRefetchingAfterAdd.current) return;
    const moreDataAvailable = data.getPartnerLinks.length === 25;
    dispatch(
      aftermarketPartnerActions.setPartnerLinks(
        [...partnerLinks, ...data.getPartnerLinks].toSorted(compareByAccountName)
      )
    );
    if (moreDataAvailable) {
      setOffset(prev => prev + 25);
    } else {
      setIsLoadingAllPages(false);
    }
  };

  const baseQueryVariables = {
    dynamicsId: account.dynamicsAccountId,
    metricsDateRange: {
      from: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd'),
    },
    limit: 25,
  };

  const { refetch: refetchPartnerLinks } = useQuery<PartnerLinksResponse>(aftermarketPartnerApi.getPartnerLinks, {
    variables: { ...baseQueryVariables, offset },
    onCompleted(data) {
      fetchLazyMode(data);
    },
  });

  /**
   * Reloads the full dealer list from scratch after a new dealer has been linked.
   *
   * Why a dedicated function instead of a plain refetch?
   * Apollo Client 3 does NOT reliably fire `onCompleted` when `refetch()` is called
   * with the same variables (known upstream bug). Since `fetchLazyMode` relies on
   * `onCompleted` to append pages, we bypass that path entirely for this case.
   *
   * Flow:
   * 1. Raise `isRefetchingAfterAdd` flag so any stale `onCompleted` callbacks from
   *    in-flight requests are ignored by `fetchLazyMode`.
   * 2. Clear Redux state and reset `offset` to 0 so the list visually resets and,
   *    critically, `setOffset(25)` below is always a genuine 0→25 state change
   *    (if offset was already 25, a no-op setOffset would never re-trigger useQuery).
   * 3. Explicitly refetch page 1 with `offset: 0` and handle the result in `.then()`
   *    — this is the reliable path that bypasses the `onCompleted` bug.
   * 4. Inside `.then()`: lower the flag, dispatch page 1 to Redux, and if page 1 is
   *    full (25 items) set offset to 25. That offset change triggers `useQuery` →
   *    `onCompleted` → `fetchLazyMode` to auto-load subsequent pages normally.
   *
   * This function is passed to `AddDealerDialog` so the dialog can await it before
   * closing, ensuring the table already shows the new dealer when the dialog disappears.
   */
  const refetchAfterDealerAdded = () => {
    isRefetchingAfterAdd.current = true;
    setIsLoadingAllPages(true);
    dispatch(aftermarketPartnerActions.setPartnerLinks([]));
    setOffset(0); // reset so setOffset(25) in .then() is always a genuine state change
    // Reset the table's internal pagination to page 1. This happens while the dialog
    // is still open (spinner visible), so the user never sees an empty page 2 after
    // the dialog closes and before the new data has fully loaded.
    setTableResetKey(prev => prev + 1);
    return refetchPartnerLinks({ ...baseQueryVariables, offset: 0 }).then(result => {
      isRefetchingAfterAdd.current = false;
      if (result.data?.getPartnerLinks) {
        const page1 = result.data.getPartnerLinks;
        dispatch(aftermarketPartnerActions.setPartnerLinks([...page1].toSorted(compareByAccountName)));
        if (page1.length === 25) {
          setOffset(25); // genuine change from 0, triggers onCompleted → fetchLazyMode for page 2+
        } else {
          setIsLoadingAllPages(false); // single page — no further fetchLazyMode calls will fire
        }
      } else {
        setIsLoadingAllPages(false);
      }
      return result;
    });
  };

  const { data: integrationSettingsAccountIsAdminOf, loading: loadingIntegrationSettingsAccountIsAdminOf } = useQuery<{
    integrationSettingsAccountIsAdminOf: IntegrationSettings[];
  }>(QUERY_INTEGRATION_SETTINGS_ACCOUNT_IS_ADMIN_OF, {
    variables: {
      accountId: account.id,
    },
    onCompleted(data) {
      dispatch(
        aftermarketPartnerActions.setIsIntegrationAdminAccount(data.integrationSettingsAccountIsAdminOf.length > 0)
      );
    },
  });

  const handleOpenIntegrationSettingsDialog = ({ partnerLink }: { partnerLink: DealerManagementPartnerLink }) => {
    openIntegrationSettingsDialog({
      integrationName: integrationSettingsAccountIsAdminOf!.integrationSettingsAccountIsAdminOf[0].integrationName,
      dealerAccountId: partnerLink.accountId,
      dealerAccountName: partnerLink.accountName,
      dealerLocations: partnerLink.partnerLocations,
      dealerContacts: [],
    });
  };

  const onRedirectToDealerDetailsPage = (partnerLinkId: Nullable<string> | undefined) => {
    if (partnerLinkId) {
      history.push(`/partner-dealers/${partnerLinkId}`);
    } else {
      logError('[onRedirectToDealerDetailsPage]', 'partnerLinkId null or undefined');
    }
  };

  useEffect(
    () => () => {
      dispatch(aftermarketPartnerActions.clearState());
    },
    []
  );

  return (
    <CardContainer
      title="Dealer Management"
      actionBtn={
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddDealerDialogOpen({ refetchAfterDealerAdded })}
        >
          Add Dealer
        </Button>
      }
    >
      <Stack gap="0.5rem">
        <DealerManagementTable
          key={tableResetKey}
          tableData={partnerLinks}
          onRowClick={onRedirectToDealerDetailsPage}
          loading={isLoadingAllPages || loadingIntegrationSettingsAccountIsAdminOf}
          handleOpenIntegrationSettingsDialog={handleOpenIntegrationSettingsDialog}
        />
      </Stack>
    </CardContainer>
  );
};
